import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} KlaviyoCartEventData
 * @property {boolean} [didError]
 * @property {string} [source]
 * @property {string | number | null} [variantId]
 * @property {string | number} [itemCount]
 */

/**
 * @typedef {object} KlaviyoCartEventDetail
 * @property {KlaviyoCart | null} [resource]
 * @property {string | number | null} [sourceId]
 * @property {KlaviyoCartEventData} [data]
 */

/**
 * @typedef {Event & { detail?: KlaviyoCartEventDetail }} KlaviyoCartEvent
 */

/**
 * @typedef {object} KlaviyoCart
 * @property {Array<KlaviyoCartItem>} [items]
 */

/**
 * @typedef {object} KlaviyoCartItem
 * @property {string | number} [id]
 * @property {string | number} [variant_id]
 * @property {string | number} [product_id]
 * @property {string} [product_title]
 * @property {string} [title]
 * @property {string} [variant_title]
 * @property {string} [sku]
 * @property {string | number} [quantity]
 * @property {string | number} [final_price]
 * @property {string | number} [price]
 * @property {string} [url]
 * @property {string} [image]
 * @property {string} [handle]
 * @property {string} [vendor]
 */

const ADD_TO_CART_SOURCES = new Set([
    'product-form-component',
    'offer-page',
]);

/**
 * @param {KlaviyoCartEvent} event
 * @returns {boolean}
 */
function checkShouldTrackAddedToCart(event) {
    const data = event.detail?.data || {};

    if (data.didError) return false;
    if (!data.source) return false;
    if (!ADD_TO_CART_SOURCES.has(data.source)) return false;

    return Boolean(data.variantId || event.detail?.sourceId);
}

/**
 * @param {KlaviyoCartEvent} event
 * @returns {string | null}
 */
function parseVariantId(event) {
    const data = event.detail?.data || {};
    const variantId = data.variantId || event.detail?.sourceId;

    return variantId ? String(variantId) : null;
}

/**
 * @param {KlaviyoCart | null | undefined} cart
 * @returns {Array<KlaviyoCartItem>}
 */
function parseCartItems(cart) {
    return Array.isArray(cart?.items) ? cart.items : [];
}

/**
 * @returns {Promise<KlaviyoCart | null>}
 */
async function fetchCart() {
    try {
        const cartUrl = Theme.routes.cart_url.endsWith('.js')
            ? Theme.routes.cart_url
            : `${Theme.routes.cart_url}.js`;
        const response = await fetch(cartUrl, {
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) return null;

        return await response.json();
    } catch (error) {
        console.warn('Unable to fetch cart for Klaviyo add-to-cart tracking.', error);
        return null;
    }
}

/**
 * @param {Array<KlaviyoCartItem>} items
 * @param {string} variantId
 * @returns {KlaviyoCartItem | null}
 */
function findCartItemByVariantId(items, variantId) {
    return items.find((item) => String(item?.id || item?.variant_id) === variantId) || null;
}

/**
 * @param {KlaviyoCartItem} item
 * @param {KlaviyoCartEvent} event
 * @returns {object}
 */
function createKlaviyoPayload(item, event) {
    const data = event.detail?.data || {};
    const addedQuantity = Number(data.itemCount || 0);
    const quantity = addedQuantity || Number(item.quantity || 1);
    const itemPrice = Number(item.final_price || item.price || 0) / 100;

    return {
        ProductName: item.product_title || item.title,
        ProductID: item.product_id,
        VariantID: item.id || item.variant_id || parseVariantId(event),
        VariantName: item.variant_title,
        SKU: item.sku,
        Quantity: quantity,
        ItemPrice: itemPrice,
        RowTotal: itemPrice * quantity,
        ProductURL: item.url,
        ImageURL: item.image,
        ProductHandle: item.handle,
        Vendor: item.vendor,
    };
}

/**
 * @param {object} payload
 */
function trackAddedToCart(payload) {
    window._learnq = window._learnq || [];
    // console.log('[Klaviyo] Added to Cart', payload);
    window._learnq.push(['track', 'Added to Cart', payload]);
}

/**
 * @param {Event} event
 */
async function handleCartUpdate(event) {
    const cartEvent = /** @type {KlaviyoCartEvent} */ (event);

    if (!checkShouldTrackAddedToCart(cartEvent)) return;

    const variantId = parseVariantId(cartEvent);
    if (!variantId) return;

    const eventCart = cartEvent.detail?.resource;
    const cart = parseCartItems(eventCart).length ? eventCart : await fetchCart();
    const item = findCartItemByVariantId(parseCartItems(cart), variantId);

    if (!item) return;

    trackAddedToCart(createKlaviyoPayload(item, cartEvent));
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
